import React, { ReactElement, useEffect, useState } from 'react'
import { Box, Flex } from 'theme-ui'
import { useHotkeys } from 'react-hotkeys-hook'
import FocusLock from 'react-focus-lock'
import { ModalDialogProps } from './ModalDialog.interface'
import ModalDialogHeader from './ModalDialogHeader'
import ModalDialogFooter from './ModalDialogFooter'
import useOnClickOutside from 'utils/useOnClickOutside'
import CreatePortalWrapper from 'components/CreatePortalWrapper'
import useBreakpointIndex from '../Theme/useBreakpointIndex'
import { useAppDispatch } from 'store/hooks'
import { setDisableFocusLockInAlert } from 'store/dashboard/dashboard'

export const modalDialogWidth = ['100%', 'fit-content', 'fit-content']

export default function ModalDialog({
  dismiss = (): void => {},
  isOpen,
  onPresentTransitionEnd,
  onDismissTransitionEnd,
  children,
  sx,
  sxOverlay,
  dataTestId = 'modal-dialog-body',
  shouldVibrateOnPresent = false,
  showOverlay = true,
  modalDialogRef,
  disableFocusLock,
  title,
  elementInsteadOfTitle,
  sxHeaderContainer,
  headerRef,
  onCloseClick,
  onBackClick,
  footer,
  isLeftContentVisible,
  isRightContentVisible,
  sxFooterContainer,
  leftContent,
  rightContent,
  sxContentContainer,
  shouldCreatePortal = true,
  shouldHideFooter,
}: ModalDialogProps): ReactElement | null {
  const isDesktop = useBreakpointIndex() === 2
  const [isFocusLockDisabled, setIsDisabledFocusLock] = useState(isDesktop)
  const dispatch = useAppDispatch()

  useOnClickOutside([], () => {
    if (isDesktop) {
      setIsDisabledFocusLock(true)
      dispatch(setDisableFocusLockInAlert(true))
    }
  })

  useHotkeys(
    'Tab',
    () => {
      setIsDisabledFocusLock(false)
      dispatch(setDisableFocusLockInAlert(false))
    },
    [],
  )

  useHotkeys(
    'Escape',
    () => {
      isOpen && dismiss()
    },
    [isOpen],
  )

  const handleTransitionEnd = (): void => {
    if (isOpen && onPresentTransitionEnd) {
      onPresentTransitionEnd()
    } else if (!isOpen && onDismissTransitionEnd) {
      onDismissTransitionEnd()
    }
  }

  useEffect(() => {
    if (shouldVibrateOnPresent && isOpen && window.navigator.vibrate) {
      try {
        window.navigator.vibrate(10)
      } catch (e) {}
    }
    // we only want to trigger this effect when `isOpen` changes, not when
    // `shouldVibrateOnPresent` changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <CreatePortalWrapper shouldCreatePortal={shouldCreatePortal}>
      <Flex
        onTransitionEnd={handleTransitionEnd}
        onClick={(): void => dismiss()}
        sx={{
          visibility: isOpen ? 'inherit' : 'hidden',
          transition: 'opacity 0.2s ease',
          backgroundColor: showOverlay ? 'eerieBlack80' : 'transparent',
          width: '100%',
          height: '100%',
          position: 'fixed',
          left: '0px',
          top: '0px',
          zIndex: 'zIndex595',
          justifyContent: 'center',
          alignItems: ['unset', 'center'],
          '& > div': {
            width: ['100%', 'auto'],
          },
          ...sxOverlay,
        }}
      >
        <FocusLock autoFocus={false} disabled={disableFocusLock || isFocusLockDisabled || !isOpen}>
          <Box
            ref={modalDialogRef}
            className={`modal-dialog ${isOpen ? 'transition' : ''}`}
            aria-modal="true"
            role="dialog"
            aria-labelledby={title ? `${title}-title` : undefined}
            aria-describedby={`${title}-content`}
            data-testid={dataTestId}
            aria-live="polite"
            aria-hidden={!isOpen}
            sx={{
              display: isOpen ? 'flex' : 'none',
              flexDirection: 'column',
              width: modalDialogWidth,
              maxHeight: ['100%', 'calc(100% - 4.8rem)'],
              height: ['100%', 'fit-content'],
              backgroundColor: 'blue800',
              borderRadius: [0, '1.2rem'],
              zIndex: 'zIndex596',
              border: '1px solid',
              borderColor: 'blueYonder30',
              boxShadow: '0px 5px 45px -5px rgba(0, 0, 0, 45%)',
              ...sx,
            }}
            tabIndex={isOpen ? 0 : -1}
            onClick={e => e.stopPropagation()}
          >
            {(title || elementInsteadOfTitle) && (
              <ModalDialogHeader
                title={title}
                elementInsteadOfTitle={elementInsteadOfTitle}
                headerRef={headerRef}
                sx={sxHeaderContainer}
                onCloseClick={onCloseClick}
                onBackClick={onBackClick}
                leftContent={leftContent}
                rightContent={rightContent}
                isLeftContentVisible={isLeftContentVisible}
                isRightContentVisible={isRightContentVisible}
              />
            )}
            <Flex
              sx={{
                flex: [1, 'auto'],
                alignItems: 'flex-start',
                backgroundColor: 'cardBg',
                overflow: 'hidden',
                borderRadius: footer ? 0 : [0, '0 0 1.2rem 1.2rem'],
                ...sxContentContainer,
              }}
            >
              {children}
            </Flex>
            {footer && (
              <ModalDialogFooter sx={sxFooterContainer} shouldHideFooter={shouldHideFooter}>
                {footer}
              </ModalDialogFooter>
            )}
          </Box>
        </FocusLock>
      </Flex>
    </CreatePortalWrapper>
  )
}
